'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMinus,
  faShoppingCart,
  faUtensils,
  faUser,
  faCrown,
  faStar as faStarSolid,
  faClock,
  faCheck,
  faCheckCircle,
  faCommentDots,
  faQrcode,
  faCashRegister,
  faPrayingHands,
  faSurprise,
  faBox,
  faClipboardCheck,
  faClone,
  faBrain,
} from '@fortawesome/free-solid-svg-icons';
// import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function HORECAHubKOT() {
  const tables = Array.from({ length: 10 }, (_, i) => i + 1);

  const menu = [
    {
      category: 'Appetizers',
      items: [
        {
          id: 1,
          name: 'Vegetable Spring Rolls',
          price: 150,
          desc: 'Crispy rolls filled with mixed vegetables',
          customizations: [
            { key: 'spice', label: 'Spice Level', options: ['Mild', 'Medium', 'Spicy'], default: 'Medium' },
            { key: 'portion', label: 'Portion', options: ['Half (4 pcs)', 'Full (8 pcs)'], priceMultiplier: [0.5, 1], default: 'Full (8 pcs)' },
          ],
        },
        {
          id: 2,
          name: 'Chicken 65',
          price: 280,
          desc: 'Spicy deep-fried chicken',
          customizations: [
            { key: 'spice', label: 'Spice Level', options: ['Mild', 'Medium', 'Spicy', 'Extra Spicy'], default: 'Spicy' },
            { key: 'type', label: 'Gravy/Dry', options: ['Dry', 'With Gravy'], default: 'Dry' },
          ],
        },
      ],
    },
    {
      category: 'Main Course',
      items: [
        {
          id: 3,
          name: 'Butter Chicken',
          price: 420,
          desc: 'Creamy tomato-based chicken curry',
          customizations: [
            { key: 'spice', label: 'Spice Level', options: ['Mild', 'Medium', 'Spicy'], default: 'Medium' },
            { key: 'gravy', label: 'Gravy', options: ['Normal', 'More Gravy', 'Dry'], default: 'Normal' },
            { key: 'portion', label: 'Serves', options: ['Single', 'For 2'], priceMultiplier: [1, 1.8], default: 'Single' },
          ],
        },
        {
          id: 4,
          name: 'Paneer Butter Masala',
          price: 380,
          desc: 'Paneer cubes in rich tomato gravy',
          customizations: [
            { key: 'spice', label: 'Spice Level', options: ['Mild', 'Medium', 'Spicy'], default: 'Medium' },
          ],
        },
      ],
    },
    {
      category: 'Desserts',
      items: [
        {
          id: 5,
          name: 'Gulab Jamun',
          price: 120,
          desc: 'Soft fried dough balls in rose syrup',
          customizations: [
            { key: 'temp', label: 'Temperature', options: ['Hot', 'Warm', 'Cold'], default: 'Hot' },
            { key: 'pieces', label: 'Pieces', options: ['2 pcs', '4 pcs', '6 pcs'], priceMultiplier: [1, 2, 3], default: '2 pcs' },
          ],
        },
      ],
    },
    {
      category: 'Beverages',
      items: [
        {
          id: 6,
          name: 'Mango Lassi',
          price: 130,
          desc: 'Sweet yogurt-based mango drink',
          customizations: [
            { key: 'sweetness', label: 'Sweetness', options: ['Less Sweet', 'Normal', 'Extra Sweet'], default: 'Normal' },
            { key: 'ice', label: 'Ice', options: ['With Ice', 'No Ice'], default: 'With Ice' },
          ],
        },
        {
          id: 7,
          name: 'Soft Drink',
          price: 60,
          desc: 'Chilled aerated drink',
          customizations: [
            { key: 'type', label: 'Type', options: ['Coke', 'Sprite', 'Fanta'], default: 'Coke' },
          ],
        },
      ],
    },
  ];

  const TAX_RATE = 0.05;

  const [currentRole, setCurrentRole] = useState(null);
  const [currentTable, setCurrentTable] = useState(null);
  const [customerView, setCustomerView] = useState('menu');
  const [showTableSelection, setShowTableSelection] = useState(false);
  const [tableStatuses, setTableStatuses] = useState(
    Object.fromEntries(tables.map((t) => [t, 'vacant']))
  );
  const [tableData, setTableData] = useState({});
  const [sales, setSales] = useState({
    total: 0,
    categories: { Appetizers: 0, 'Main Course': 0, Desserts: 0, Beverages: 0 },
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [tempQty, setTempQty] = useState(1);
  const [tempCustom, setTempCustom] = useState({});
  const [tempNote, setTempNote] = useState('');
  const [chefNoteInput, setChefNoteInput] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');

  const getDefaultCustom = (item) => {
    const def = {};
    if (item.customizations) {
      item.customizations.forEach((c) => {
        def[c.key] = c.default;
      });
    }
    return def;
  };

  const calculateItemPrice = (item, qty, selectedCustom) => {
    let price = item.price * qty;
    if (item.customizations) {
      item.customizations.forEach((c) => {
        if (c.priceMultiplier) {
          const selected = selectedCustom[c.key] || c.default;
          const idx = c.options.indexOf(selected);
          if (idx !== -1) price *= c.priceMultiplier[idx];
        }
      });
    }
    return Math.round(price);
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, entry) => sum + calculateItemPrice(entry.item, entry.qty, entry.selectedCustom || {}), 0);
  };

  const getTableData = (table) => tableData[table] || { cart: [], confirmedItems: [], chefNotes: [], tip: 0, paymentMethod: 'Cash', billRequested: false, paid: false };

  // Role selection screen
  if (!currentRole) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
        <h1 className="text-2xl lg:text-3xl font-bold mb-12 text-center">🍽️ HORECA Dine-In</h1>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 w-full place-items-center">
          <button
            onClick={() => setCurrentRole('Entrance')}
            className="bg-gray-800 text-purple-600 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faQrcode} size="2x" className="mb-4" />
            <span className="font-bold">Welcome</span>
          </button>            
          <button
            onClick={() => setCurrentRole('customer')}
            className="bg-gray-800 text-green-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faUtensils} size="2x" className="mb-4" />
            <span className="font-bold">Customer</span>
          </button>
          <button
            onClick={() => setCurrentRole('waiter')}
            className="bg-gray-800 text-blue-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faUser} size="2x" className="mb-4" />
            <span className="font-bold">Waiter</span>
          </button>
          <button
            onClick={() => setCurrentRole('chef')}
            className="bg-gray-800 text-red-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faUtensils} size="2x" className="mb-4" />
            <span className="font-bold">Chef</span>
          </button>
          <button
            onClick={() => setCurrentRole('cashier')}
            className="bg-gray-800 text-yellow-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faCashRegister} size="2x" className="mb-4" />
            <span className="font-bold">Cashier</span>
          </button>    
          <button
            onClick={() => setCurrentRole('thankyou')}
            className="bg-gray-800 text-purple-500 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faPrayingHands} size="2x" className="mb-4" />
            <span className="font-bold">Visit Again</span>
          </button>    
          <button
            onClick={() => setCurrentRole('suppliers')}
            className="bg-gray-800 text-white rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faBox} size="2x" className="mb-4" />
            <span className="font-bold">Suppliers</span>
          </button>                      
          <button
            onClick={() => setCurrentRole('owner')}
            className="bg-gray-800 text-yellow-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faCrown} size="2x" className="mb-4" />
            <span className="font-bold">Owner</span>
          </button>
          <button
            onClick={() => setCurrentRole('foodinspector')}
            className="bg-gray-800 text-blue-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faClipboardCheck} size="2x" className="mb-4" />
            <span className="font-bold">Food Inspector</span>
          </button>      
          <button
            onClick={() => setCurrentRole('digitaltwin')}
            className="bg-gray-800 text-green-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faClone} size="2x" className="mb-4" />
            <span className="font-bold">Digital Twin</span>
          </button>   
          <button
            onClick={() => setCurrentRole('thinkyai')}
            className="bg-gray-800 text-cyan-400 rounded-lg shadow-lg p-2 flex flex-col items-center hover:bg-gray-700 transition text-xs lg:text-sm w-40 lg:w-64"
          >
            <FontAwesomeIcon icon={faBrain} size="2x" className="mb-4" />
            <span className="font-bold">Thinky AI</span>
          </button>                              
        </div>
      </div>
    );
  }

  // ===================== CUSTOMER ROLE =====================
  if (currentRole === 'customer') {
    const data = currentTable ? getTableData(currentTable) : null;

    return (
      <div className="min-h-screen bg-black flex flex-col text-white">
        <header className="bg-gray-900 p-6 text-center">
          <h1 className="text-3xl font-bold">
            {!currentTable
              ? 'Welcome to HORECA Restaurant'
              : data.paid
              ? 'Thank You!'
              : `Table ${currentTable}`}
          </h1>
        </header>

        {!currentTable ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <p className="text-xl mb-12 text-center">Scan the QR code at the entrance gate</p>
            <button
              onClick={() => setShowTableSelection(true)}
              className="bg-green-600 text-white px-12 py-6 rounded-xl shadow-lg text-xs lg:text-sm w-40 lg:w-64"
            >
              Simulate Entry Scan
            </button>

            {showTableSelection && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                <div className="bg-gray-800 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                  <h2 className="text-3xl font-bold mb-8 text-center">Choose Your Table</h2>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-6">
                    {tables.map((t) => (
                      <button
                        key={t}
                        disabled={tableStatuses[t] !== 'vacant'}
                        onClick={() => {
                          setCurrentTable(t);
                          setTableStatuses((prev) => ({ ...prev, [t]: 'occupied' }));
                          setTableData((prev) => ({
                            ...prev,
                            [t]: {
                              cart: [],
                              confirmedItems: [],
                              chefNotes: [],
                              tip: 0,
                              paymentMethod: 'Cash',
                              billRequested: false,
                              paid: false,
                            },
                          }));
                          setShowTableSelection(false);
                        }}
                        className={`py-8 rounded-xl text-2xl font-bold transition ${
                          tableStatuses[t] === 'vacant'
                            ? 'bg-green-600 text-white hover:bg-green-500'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowTableSelection(false)}
                    className="mt-8 text-red-400 text-xl"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : data.paid ? (
          <div className="flex-1 p-8">
            <p className="text-2xl text-center mb-12">
              We hope you enjoyed your meal at Table {currentTable}
            </p>

            <h2 className="text-2xl font-bold mb-6 text-center">Rate Your Experience</h2>
            <div className="flex justify-center gap-4 mb-12">
              {[1, 2, 3, 4, 5].map((s) => (
                <FontAwesomeIcon
                  key={s}
                  icon={s <= feedbackRating ? faStarSolid : faStarRegular}
                  size="3x"
                  className="text-yellow-400 cursor-pointer"
                  onClick={() => setFeedbackRating(s)}
                />
              ))}
            </div>

            <textarea
              placeholder="Any additional feedback?"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              className="w-full p-4 bg-gray-800 border border-gray-700 rounded-xl h-32 mb-8 text-white"
            />

            <button
              onClick={() => {
                setFeedbackRating(0);
                setFeedbackComment('');
                setTableStatuses((prev) => ({ ...prev, [currentTable]: 'vacant' }));
                setCurrentTable(null);
                setCurrentRole(null);
              }}
              className="bg-green-600 text-white py-6 rounded-xl font-bold text-xs lg:text-sm w-40 lg:w-64 mx-auto block"
            >
              Submit & Exit
            </button>

            <p className="text-center mt-8 text-gray-400">
              Please scan the exit QR to sign off
            </p>
          </div>
        ) : (
          <>
            <header className="bg-gray-900 p-4 flex justify-center">
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => setCustomerView('menu')}
                  className={`px-6 py-3 rounded-xl font-medium transition text-xs lg:text-sm w-40 lg:w-64 ${
                    customerView === 'menu' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Menu
                </button>
                <button
                  onClick={() => setCustomerView('cart')}
                  className={`px-6 py-3 rounded-xl font-medium transition relative text-xs lg:text-sm w-40 lg:w-64 ${
                    customerView === 'cart' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Cart
                  {data.cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center">
                      {data.cart.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setCustomerView('orders')}
                  className={`px-6 py-3 rounded-xl font-medium transition text-xs lg:text-sm w-40 lg:w-64 ${
                    customerView === 'orders' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Orders
                </button>
                <button
                  onClick={() => setCustomerView('bill')}
                  className={`px-6 py-3 rounded-xl font-medium transition text-xs lg:text-sm w-40 lg:w-64 ${
                    customerView === 'bill' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Bill
                </button>
              </div>
            </header>

            <div className="flex-1 p-4 overflow-y-auto pb-20">
              {/* MENU VIEW */}
              {customerView === 'menu' && (
                <>
                  <p className="text-center text-lg mb-6 text-gray-400">Scan the table QR code to begin ordering</p>
                  <div className="space-y-12">
                    {menu.map((cat) => (
                      <div key={cat.category}>
                        <h2 className="text-3xl font-bold mb-6 text-center">{cat.category}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {cat.items.map((item) => (
                            <div key={item.id} className="bg-gray-800 rounded-2xl shadow-lg p-6 flex flex-col">
                              <div className="flex-1">
                                <h3 className="text-xl font-bold">{item.name}</h3>
                                <p className="text-gray-400 my-2">{item.desc}</p>
                                <p className="text-2xl font-bold text-green-400">₹{item.price}</p>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedItem(item);
                                  setTempQty(1);
                                  setTempCustom(getDefaultCustom(item));
                                  setTempNote('');
                                }}
                                className="mt-6 bg-green-600 text-white py-4 rounded-xl flex items-center justify-center font-medium text-xs lg:text-sm"
                              >
                                <FontAwesomeIcon icon={faPlus} className="mr-3" />
                                Add to Cart
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* CART VIEW */}
              {customerView === 'cart' && (
                <div>
                  <h2 className="text-3xl font-bold mb-6 text-center">Your Cart</h2>
                  {data.cart.length === 0 ? (
                    <p className="text-center text-xl text-gray-400">Cart is empty</p>
                  ) : (
                    <>
                      {data.cart.map((entry, idx) => (
                        <div key={idx} className="bg-gray-800 rounded-2xl shadow p-6 mb-6">
                          <div className="flex justify-between">
                            <div>
                              <p className="text-xl font-bold">
                                {entry.qty} × {entry.item.name}
                              </p>
                              <p className="text-gray-400">
                                {Object.entries(entry.selectedCustom || {})
                                  .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                                  .join(' | ')}
                                {entry.note && ` | Note: ${entry.note}`}
                              </p>
                            </div>
                            <p className="text-xl font-bold">₹{calculateItemPrice(entry.item, entry.qty, entry.selectedCustom || {})}</p>
                          </div>
                          <div className="flex gap-4 mt-4">
                            <button
                              onClick={() => {
                                setSelectedItem(entry.item);
                                setTempQty(entry.qty);
                                setTempCustom(entry.selectedCustom || {});
                                setTempNote(entry.note || '');
                                setTableData((prev) => {
                                  const d = { ...prev[currentTable] };
                                  d.cart.splice(idx, 1);
                                  return { ...prev, [currentTable]: d };
                                });
                              }}
                              className="bg-blue-600 text-white px-6 py-3 rounded-xl text-xs lg:text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setTableData((prev) => {
                                  const d = { ...prev[currentTable] };
                                  d.cart.splice(idx, 1);
                                  return { ...prev, [currentTable]: d };
                                });
                              }}
                              className="bg-red-600 text-white px-6 py-3 rounded-xl text-xs lg:text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      <p className="text-2xl font-bold text-right mb-8">
                        Cart Total: ₹{data.cart.reduce((s, e) => s + calculateItemPrice(e.item, e.qty, e.selectedCustom || {}), 0)}
                      </p>
                      <button
                        onClick={() => {
                          setTableData((prev) => {
                            const d = { ...prev[currentTable] };
                            d.confirmedItems = [
                              ...d.confirmedItems,
                              ...d.cart.map((e) => ({ ...e, status: 'ordered' })),
                            ];
                            d.cart = [];
                            return { ...prev, [currentTable]: d };
                          });
                          setCustomerView('orders');
                        }}
                        disabled={data.cart.length === 0}
                        className="w-full bg-green-600 text-white py-6 rounded-xl disabled:bg-gray-700 text-xs lg:text-sm"
                      >
                        Confirm & Send to Kitchen
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* ORDERS VIEW */}
              {customerView === 'orders' && (
                <div>
                  <h2 className="text-3xl font-bold mb-6 text-center">Order Status</h2>
                  {data.confirmedItems.length === 0 ? (
                    <p className="text-center text-xl text-gray-400">No orders placed yet</p>
                  ) : (
                    <>
                      {data.confirmedItems.map((entry, idx) => (
                        <div key={idx} className="bg-gray-800 rounded-2xl shadow p-6 mb-6 flex justify-between items-center">
                          <div>
                            <p className="text-xl font-bold">
                              {entry.qty} × {entry.item.name}
                            </p>
                            <p className="text-gray-400">
                              {Object.entries(entry.selectedCustom || {})
                                .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                                .join(' | ')}
                            </p>
                            <p className="mt-2 flex items-center gap-3 text-lg">
                              Status:
                              {entry.status === 'ordered' && <FontAwesomeIcon icon={faClock} className="text-yellow-400" />}
                              {entry.status === 'preparing' && <FontAwesomeIcon icon={faCheck} className="text-orange-400" />}
                              {entry.status === 'ready' && <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />}
                              {entry.status === 'served' && <FontAwesomeIcon icon={faCheckCircle} className="text-blue-400" />}
                              <span className="capitalize">{entry.status}</span>
                            </p>
                          </div>
                          <p className="text-xl font-bold">₹{calculateItemPrice(entry.item, entry.qty, entry.selectedCustom || {})}</p>
                        </div>
                      ))}
                      <p className="text-2xl font-bold text-right mb-8">
                        Total: ₹{calculateSubtotal(data.confirmedItems)}
                      </p>
                      {!data.billRequested && (
                        <button
                          onClick={() => {
                            setTableData((prev) => ({
                              ...prev,
                              [currentTable]: { ...prev[currentTable], billRequested: true },
                            }));
                            setCustomerView('bill');
                          }}
                          className="w-full bg-green-600 text-white py-6 rounded-xl text-xs lg:text-sm"
                        >
                          Request Bill
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* BILL VIEW */}
              {customerView === 'bill' && (
                <div>
                  {data.billRequested ? (
                    <>
                      <h2 className="text-3xl font-bold mb-6 text-center">Your Bill - Table {currentTable}</h2>
                      {data.confirmedItems.map((entry, idx) => (
                        <div key={idx} className="flex justify-between py-3 border-b border-gray-700">
                          <span>
                            {entry.qty} × {entry.item.name}{' '}
                            {Object.entries(entry.selectedCustom || {}).length > 0 && (
                              <span className="text-gray-400 text-sm">
                                ({Object.entries(entry.selectedCustom || {})
                                  .map(([_, v]) => `${v}`)
                                  .join(', ')})
                              </span>
                            )}
                          </span>
                          <span className="font-bold">₹{calculateItemPrice(entry.item, entry.qty, entry.selectedCustom || {})}</span>
                        </div>
                      ))}
                      <div className="mt-8 space-y-4 text-xl">
                        <div className="flex justify-between">
                          <span>Subtotal</span>
                          <span>₹{calculateSubtotal(data.confirmedItems)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Tax (5%)</span>
                          <span>₹{Math.round(calculateSubtotal(data.confirmedItems) * TAX_RATE)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Tip</span>
                          <span>₹{data.tip || 0}</span>
                        </div>
                        <div className="flex justify-between text-2xl font-bold border-t-2 border-gray-700 pt-4">
                          <span>Grand Total</span>
                          <span>₹{calculateSubtotal(data.confirmedItems) + Math.round(calculateSubtotal(data.confirmedItems) * TAX_RATE) + (data.tip || 0)}</span>
                        </div>
                      </div>

                      <div className="mt-12">
                        <h3 className="text-2xl font-bold mb-4">Add Tip</h3>
                        <div className="grid grid-cols-4 gap-4 mb-6">
                          {['No Tip', '10%', '15%', '20%'].map((label, i) => (
                            <button
                              key={label}
                              onClick={() => {
                                const tip = i === 0 ? 0 : Math.round(calculateSubtotal(data.confirmedItems) * (0.1 * i));
                                setTableData((prev) => ({ ...prev, [currentTable]: { ...prev[currentTable], tip } }));
                              }}
                              className="bg-gray-700 py-4 rounded-xl text-xs lg:text-sm hover:bg-gray-600"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          placeholder="Custom tip amount"
                          onChange={(e) =>
                            setTableData((prev) => ({
                              ...prev,
                              [currentTable]: { ...prev[currentTable], tip: parseInt(e.target.value) || 0 },
                            }))
                          }
                          className="w-full p-4 bg-gray-800 border border-gray-700 rounded-xl text-xl"
                        />
                      </div>

                      <div className="mt-12">
                        <h3 className="text-2xl font-bold mb-4">Payment Method</h3>
                        <select
                          value={data.paymentMethod}
                          onChange={(e) =>
                            setTableData((prev) => ({
                              ...prev,
                              [currentTable]: { ...prev[currentTable], paymentMethod: e.target.value },
                            }))
                          }
                          className="w-full p-4 bg-gray-800 border border-gray-700 rounded-xl text-xl mb-8"
                        >
                          <option>Cash</option>
                          <option>Card</option>
                          <option>UPI</option>
                        </select>
                        <button
                          onClick={() => {
                            const subtotal = calculateSubtotal(data.confirmedItems);
                            const grandTotal = subtotal + Math.round(subtotal * TAX_RATE) + (data.tip || 0);
                            setSales((prev) => {
                              const newCat = { ...prev.categories };
                              data.confirmedItems.forEach((e) => {
                                newCat[e.item.category] += calculateItemPrice(e.item, e.qty, e.selectedCustom || {});
                              });
                              return { total: prev.total + grandTotal, categories: newCat };
                            });
                            setTableData((prev) => ({
                              ...prev,
                              [currentTable]: { ...prev[currentTable], paid: true },
                            }));
                          }}
                          className="w-full bg-green-600 text-white py-8 rounded-xl font-bold text-xs lg:text-sm"
                        >
                          Pay Now
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-2xl text-gray-400">Request bill from the Orders tab when ready</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ADD/EDIT ITEM MODAL */}
        {currentTable && !data?.paid && selectedItem && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-3xl font-bold mb-4">{selectedItem.name}</h2>
            <p className="text-gray-400 mb-6">{selectedItem.desc}</p>
            <p className="text-2xl font-bold text-green-400 mb-8">
              Base Price: ₹{selectedItem.price}
            </p>

            {/* Quantity */}
            <div className="mb-8">
              <label className="text-xl font-bold block mb-3">Quantity</label>
              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={() => setTempQty(Math.max(1, tempQty - 1))}
                  className="bg-gray-700 w-16 h-16 rounded-full text-3xl hover:bg-gray-600"
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <span className="text-4xl font-bold w-24 text-center">{tempQty}</span>
                <button
                  onClick={() => setTempQty(tempQty + 1)}
                  className="bg-gray-700 w-16 h-16 rounded-full text-3xl hover:bg-gray-600"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>

            {/* Customizations */}
            {selectedItem.customizations?.map((c) => (
              <div key={c.key} className="mb-8">
                <label className="text-xl font-bold block mb-3">{c.label}</label>
                <select
                  value={tempCustom[c.key] || c.default}
                  onChange={(e) =>
                    setTempCustom((prev) => ({ ...prev, [c.key]: e.target.value }))
                  }
                  className="w-full p-4 bg-gray-700 border border-gray-600 rounded-xl text-lg"
                >
                  {c.options.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
            ))}

            {/* Special instructions */}
            <div className="mb-8">
              <label className="text-xl font-bold block mb-3">Special Instructions</label>
              <textarea
                placeholder="e.g., extra spicy, no onions, etc."
                value={tempNote}
                onChange={(e) => setTempNote(e.target.value)}
                className="w-full p-4 bg-gray-700 border border-gray-600 rounded-xl h-28"
              />
            </div>

            <p className="text-2xl font-bold text-center mb-8">
              Item Total: ₹{calculateItemPrice(selectedItem, tempQty, tempCustom)}
            </p>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setTempQty(1);
                  setTempCustom({});
                  setTempNote('');
                }}
                className="flex-1 bg-red-600 text-white py-5 rounded-xl text-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setTableData((prev) => {
                    const newTableData = { ...prev };
                    const tableKey = currentTable;

                    // Ensure table entry exists
                    if (!newTableData[tableKey]) {
                      newTableData[tableKey] = {
                        cart: [],
                        confirmedItems: [],
                        chefNotes: [],
                        tip: 0,
                        paymentMethod: 'Cash',
                        billRequested: false,
                        paid: false,
                      };
                    }

                    const cart = newTableData[tableKey].cart;

                    // Create identifiers for comparison
                    const newCustomStr = JSON.stringify(tempCustom);
                    const newNote = tempNote.trim();

                    // Find if an identical item already exists (same item, customizations, and note)
                    const existingIndex = cart.findIndex((entry) => 
                      entry.item.id === selectedItem.id &&
                      JSON.stringify(entry.selectedCustom) === newCustomStr &&
                      (entry.note || '').trim() === newNote
                    );

                    if (existingIndex !== -1) {
                      // Merge: increase quantity
                      cart[existingIndex].qty += tempQty;
                    } else {
                      // Add new entry
                      cart.push({
                        item: selectedItem,
                        qty: tempQty,
                        selectedCustom: { ...tempCustom },
                        note: tempNote,
                      });
                    }

                    return newTableData;
                  });

                  // Reset modal state
                  setSelectedItem(null);
                  setTempQty(1);
                  setTempCustom({});
                  setTempNote('');
                }}
                className="flex-1 bg-green-600 text-white py-5 rounded-xl text-xl"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  }

  // ===================== CHEF ROLE =====================
  if (currentRole === 'chef') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-red-600 text-white p-6 text-center">
          <h1 className="text-3xl font-bold">Chef Kitchen Display</h1>
        </header>
        <div className="flex-1 p-6 overflow-y-auto">
          {tables
            .filter((t) => tableStatuses[t] === 'occupied' && getTableData(t).confirmedItems.some((i) => i.status === 'ordered' || i.status === 'preparing'))
            .map((t) => {
              const td = getTableData(t);
              return (
                <div key={t} className="bg-white rounded-2xl shadow-lg p-8 mb-10">
                  <h2 className="text-3xl font-bold mb-6">Table {t} - KOT</h2>
                  {td.confirmedItems
                    .filter((i) => i.status !== 'ready' && i.status !== 'served')
                    .map((entry, idx) => (
                      <div key={idx} className="bg-gray-100 rounded-xl p-6 mb-6 flex justify-between items-start">
                        <div>
                          <p className="text-2xl font-bold">
                            {entry.qty} × {entry.item.name}
                          </p>
                          <p className="text-gray-700 mt-2">
                            {Object.entries(entry.selectedCustom)
                              .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                              .join(' | ')}
                          </p>
                          {entry.note && <p className="text-red-600 font-semibold mt-2">Note: {entry.note}</p>}
                          <p className="mt-3 text-lg">Status: <span className="font-bold capitalize">{entry.status}</span></p>
                        </div>
                        <div className="flex flex-col gap-3">
                          {entry.status === 'ordered' && (
                            <button
                              onClick={() => {
                                setTableData((prev) => {
                                  const d = { ...prev[t] };
                                  d.confirmedItems[idx].status = 'preparing';
                                  return { ...prev, [t]: d };
                                });
                              }}
                              className="bg-orange-600 text-white px-8 py-4 rounded-xl text-xl"
                            >
                              Start Prep
                            </button>
                          )}
                          {entry.status === 'preparing' && (
                            <button
                              onClick={() => {
                                setTableData((prev) => {
                                  const d = { ...prev[t] };
                                  d.confirmedItems[idx].status = 'ready';
                                  return { ...prev, [t]: d };
                                });
                              }}
                              className="bg-green-600 text-white px-8 py-4 rounded-xl text-xl"
                            >
                              Mark Ready
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                  <div className="mt-8 border-t-2 pt-8">
                    <h3 className="text-2xl font-bold mb-4">Voice Steps / Notes</h3>
                    <div className="flex gap-4 mb-6">
                      <input
                        type="text"
                        placeholder='e.g., "Marinate chicken for 10 mins"'
                        value={chefNoteInput}
                        onChange={(e) => setChefNoteInput(e.target.value)}
                        className="flex-1 p-4 border-2 rounded-xl text-lg"
                      />
                      <button
                        onClick={() => {
                          if (chefNoteInput.trim()) {
                            setTableData((prev) => ({
                              ...prev,
                              [t]: { ...prev[t], chefNotes: [...(prev[t]?.chefNotes || []), chefNoteInput] },
                            }));
                            setChefNoteInput('');
                          }
                        }}
                        className="bg-blue-600 text-white px-8 py-4 rounded-xl text-xl"
                      >
                        Add Step
                      </button>
                    </div>
                    <ul className="space-y-3">
                      {td.chefNotes.map((note, i) => (
                        <li key={i} className="bg-gray-100 p-4 rounded-xl text-lg">
                          {i + 1}. {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          {tables.filter((t) => tableStatuses[t] === 'occupied' && getTableData(t).confirmedItems.some((i) => i.status === 'ordered' || i.status === 'preparing')).length === 0 && (
            <p className="text-center text-2xl text-gray-500 mt-20">No active orders</p>
          )}
        </div>
      </div>
    );
  }

  // ===================== WAITER ROLE =====================
  if (currentRole === 'waiter') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-blue-600 text-white p-6 text-center">
          <h1 className="text-3xl font-bold">Waiter Service View</h1>
        </header>
        <div className="flex-1 p-6 overflow-y-auto">
          <h2 className="text-3xl font-bold mb-8">Ready to Serve</h2>
          {tables
            .filter((t) => getTableData(t).confirmedItems.some((i) => i.status === 'ready'))
            .map((t) => (
              <div key={t} className="bg-white rounded-2xl shadow-lg p-8 mb-10">
                <h3 className="text-3xl font-bold mb-6">Table {t}</h3>
                {getTableData(t).confirmedItems
                  .filter((i) => i.status === 'ready')
                  .map((entry, idx) => (
                    <div key={idx} className="bg-green-100 rounded-xl p-6 mb-6 flex justify-between items-center">
                      <div>
                        <p className="text-2xl font-bold">
                          {entry.qty} × {entry.item.name}
                        </p>
                        <p className="text-gray-700">
                          {Object.entries(entry.selectedCustom)
                            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                            .join(' | ')}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setTableData((prev) => {
                            const d = { ...prev[t] };
                            d.confirmedItems[idx].status = 'served';
                            return { ...prev, [t]: d };
                          });
                        }}
                        className="bg-blue-600 text-white px-10 py-5 rounded-xl text-xl"
                      >
                        Served
                      </button>
                    </div>
                  ))}
              </div>
            ))}
          {tables.filter((t) => getTableData(t).confirmedItems.some((i) => i.status === 'ready')).length === 0 && (
            <p className="text-center text-2xl text-gray-500 mt-20">No items ready for service</p>
          )}
        </div>
      </div>
    );
  }

  // ===================== OWNER ROLE =====================
  if (currentRole === 'owner') {
    const chartData = {
      labels: Object.keys(sales.categories),
      datasets: [
        {
          data: Object.values(sales.categories),
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
        },
      ],
    };

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-yellow-600 text-white p-6 text-center">
          <h1 className="text-3xl font-bold">Owner Dashboard</h1>
        </header>
        <div className="flex-1 p-6 overflow-y-auto">
                   <div className="space-y-12">
            {/* Today's Sales Summary */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold mb-8 text-center">Today's Total Sales</h2>
              <p className="text-5xl font-bold text-green-600 text-center mb-8">₹{sales.total}</p>
              
              <h3 className="text-2xl font-bold mb-6 text-center">Sales by Category</h3>
              <div className="w-full max-w-md mx-auto">
                <Pie 
                  data={chartData} 
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          font: { size: 16 },
                          padding: 20,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Table Overview Grid */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold mb-8 text-center">Table Status Overview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                {tables.map((t) => {
                  const status = tableStatuses[t];
                  const td = getTableData(t);
                  const subtotal = td.confirmedItems ? calculateSubtotal(td.confirmedItems) : 0;
                  const tax = Math.round(subtotal * TAX_RATE);
                  const tip = td.tip || 0;
                  const tableTotal = subtotal + tax + tip;
                  const isPaid = td.paid;

                  return (
                    <div
                      key={t}
                      className={`rounded-2xl p-6 text-center shadow-lg transition ${
                        status === 'vacant'
                          ? 'bg-gray-300 text-gray-700'
                          : isPaid
                          ? 'bg-blue-500 text-white'
                          : 'bg-green-600 text-white'
                      }`}
                    >
                      <p className="text-3xl font-bold">Table {t}</p>
                      <p className="text-xl mt-4 capitalize">{status}</p>
                      {status === 'occupied' && !isPaid && (
                        <p className="text-2xl mt-4 font-bold">₹{tableTotal}</p>
                      )}
                      {isPaid && <p className="text-xl mt-4">Paid</p>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active Tables Detailed View */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold mb-8 text-center">Active Orders (Table-wise)</h2>
              {tables.filter((t) => tableStatuses[t] === 'occupied').length === 0 ? (
                <p className="text-center text-2xl text-gray-500">No active tables</p>
              ) : (
                <div className="space-y-10">
                  {tables
                    .filter((t) => tableStatuses[t] === 'occupied')
                    .map((t) => {
                      const td = getTableData(t);
                      const subtotal = calculateSubtotal(td.confirmedItems);
                      const tax = Math.round(subtotal * TAX_RATE);
                      const total = subtotal + tax + (td.tip || 0);

                      return (
                        <div key={t} className="border-2 border-green-600 rounded-2xl p-6">
                          <h3 className="text-3xl font-bold mb-4 text-center">Table {t}</h3>
                          {td.paid ? (
                            <p className="text-2xl text-center text-blue-600 font-bold">Bill Paid (₹{total})</p>
                          ) : td.billRequested ? (
                            <p className="text-2xl text-center text-orange-600 font-bold">Bill Requested (₹{total})</p>
                          ) : (
                            <>
                              <div className="space-y-4 mb-6">
                                {td.confirmedItems.map((entry, idx) => (
                                  <div key={idx} className="bg-gray-100 rounded-xl p-4 flex justify-between">
                                    <div>
                                      <p className="text-xl font-semibold">
                                        {entry.qty} × {entry.item.name}
                                      </p>
                                      <p className="text-gray-600">
                                        {Object.entries(entry.selectedCustom)
                                          .map(([k, v]) => `${v}`)
                                          .join(' | ')}
                                        {entry.note && ` | Note: ${entry.note}`}
                                      </p>
                                      <p className="text-lg mt-2 capitalize">Status: {entry.status}</p>
                                    </div>
                                    <p className="text-xl font-bold">₹{calculateItemPrice(entry.item, entry.qty, entry.selectedCustom)}</p>
                                  </div>
                                ))}
                              </div>
                              <div className="text-right space-y-2 text-xl">
                                <p>Subtotal: ₹{subtotal}</p>
                                <p>Tax (5%): ₹{tax}</p>
                                <p>Tip: ₹{td.tip || 0}</p>
                                <p className="text-3xl font-bold border-t-2 pt-4">Total: ₹{total}</p>
                              </div>
                              {td.chefNotes.length > 0 && (
                                <div className="mt-6 border-t-2 pt-6">
                                  <h4 className="text-xl font-bold mb-3">Chef Notes</h4>
                                  <ol className="list-decimal list-inside space-y-2">
                                    {td.chefNotes.map((note, i) => (
                                      <li key={i} className="text-gray-700">{note}</li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={() => {
                setCurrentRole(null);
                setCurrentTable(null);
              }}
              className="w-full bg-red-600 text-white py-6 rounded-2xl text-3xl font-bold shadow-lg"
            >
              Logout / Switch Role
            </button>
          </div>
        </div>
      </div>
    );
  }
  
// ===================== CHEF ROLE =====================
if (currentRole === 'chef') {
  return (
    <div className="min-h-screen bg-black flex flex-col text-white">
      <header className="bg-red-900 p-6 text-center">
        <h1 className="text-3xl font-bold">Chef Kitchen Display</h1>
      </header>
      <div className="flex-1 p-6 overflow-y-auto">
        {tables
          .filter((t) => tableStatuses[t] === 'occupied' && getTableData(t).confirmedItems.some((i) => i.status === 'ordered' || i.status === 'preparing'))
          .map((t) => {
            const td = getTableData(t);
            return (
              <div key={t} className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-10">
                <h2 className="text-3xl font-bold mb-6 text-center">Table {t} - KOT</h2>
                {td.confirmedItems
                  .filter((i) => i.status !== 'ready' && i.status !== 'served')
                  .map((entry, idx) => (
                    <div key={idx} className="bg-gray-700 rounded-xl p-6 mb-6 flex justify-between items-start">
                      <div>
                        <p className="text-2xl font-bold">
                          {entry.qty} × {entry.item.name}
                        </p>
                        <p className="text-gray-300 mt-2">
                          {Object.entries(entry.selectedCustom || {})
                            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                            .join(' | ')}
                        </p>
                        {entry.note && <p className="text-red-400 font-semibold mt-2">Note: {entry.note}</p>}
                        <p className="mt-3 text-lg">Status: <span className="font-bold capitalize">{entry.status}</span></p>
                      </div>
                      <div className="flex flex-col gap-3">
                        {entry.status === 'ordered' && (
                          <button
                            onClick={() => {
                              setTableData((prev) => {
                                const d = { ...prev[t] };
                                d.confirmedItems[idx].status = 'preparing';
                                return { ...prev, [t]: d };
                              });
                            }}
                            className="bg-orange-600 text-white px-8 py-4 rounded-xl text-xl hover:bg-orange-500"
                          >
                            Start Prep
                          </button>
                        )}
                        {entry.status === 'preparing' && (
                          <button
                            onClick={() => {
                              setTableData((prev) => {
                                const d = { ...prev[t] };
                                d.confirmedItems[idx].status = 'ready';
                                return { ...prev, [t]: d };
                              });
                            }}
                            className="bg-green-600 text-white px-8 py-4 rounded-xl text-xl hover:bg-green-500"
                          >
                            Mark Ready
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                <div className="mt-8 border-t-2 border-gray-600 pt-8">
                  <h3 className="text-2xl font-bold mb-4">Voice Steps / Notes</h3>
                  <div className="flex gap-4 mb-6">
                    <input
                      type="text"
                      placeholder='e.g., "Marinate chicken for 10 mins"'
                      value={chefNoteInput}
                      onChange={(e) => setChefNoteInput(e.target.value)}
                      className="flex-1 p-4 bg-gray-700 border border-gray-600 rounded-xl text-lg placeholder-gray-400"
                    />
                    <button
                      onClick={() => {
                        if (chefNoteInput.trim()) {
                          setTableData((prev) => ({
                            ...prev,
                            [t]: { ...prev[t], chefNotes: [...(prev[t]?.chefNotes || []), chefNoteInput] },
                          }));
                          setChefNoteInput('');
                        }
                      }}
                      className="bg-blue-600 text-white px-8 py-4 rounded-xl text-xl hover:bg-blue-500"
                    >
                      Add Step
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {td.chefNotes.map((note, i) => (
                      <li key={i} className="bg-gray-700 p-4 rounded-xl text-lg">
                        {i + 1}. {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        {tables.filter((t) => tableStatuses[t] === 'occupied' && getTableData(t).confirmedItems.some((i) => i.status === 'ordered' || i.status === 'preparing')).length === 0 && (
          <p className="text-center text-2xl text-gray-400 mt-20">No active orders</p>
        )}
      </div>
    </div>
  );
}

// ===================== WAITER ROLE =====================
if (currentRole === 'waiter') {
  return (
    <div className="min-h-screen bg-black flex flex-col text-white">
      <header className="bg-blue-900 p-6 text-center">
        <h1 className="text-3xl font-bold">Waiter Service View</h1>
      </header>
      <div className="flex-1 p-6 overflow-y-auto">
        <h2 className="text-3xl font-bold mb-8 text-center">Ready to Serve</h2>
        {tables
          .filter((t) => getTableData(t).confirmedItems.some((i) => i.status === 'ready'))
          .map((t) => (
            <div key={t} className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-10">
              <h3 className="text-3xl font-bold mb-6 text-center">Table {t}</h3>
              {getTableData(t).confirmedItems
                .filter((i) => i.status === 'ready')
                .map((entry, idx) => (
                  <div key={idx} className="bg-green-900 rounded-xl p-6 mb-6 flex justify-between items-center">
                    <div>
                      <p className="text-2xl font-bold">
                        {entry.qty} × {entry.item.name}
                      </p>
                      <p className="text-gray-300">
                        {Object.entries(entry.selectedCustom || {})
                          .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                          .join(' | ')}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setTableData((prev) => {
                          const d = { ...prev[t] };
                          d.confirmedItems[idx].status = 'served';
                          return { ...prev, [t]: d };
                        });
                      }}
                      className="bg-blue-600 text-white px-10 py-5 rounded-xl text-xl hover:bg-blue-500"
                    >
                      Served
                    </button>
                  </div>
                ))}
            </div>
          ))}
        {tables.filter((t) => getTableData(t).confirmedItems.some((i) => i.status === 'ready')).length === 0 && (
          <p className="text-center text-2xl text-gray-400 mt-20">No items ready for service</p>
        )}
      </div>
    </div>
  );
}

// ===================== OWNER ROLE =====================
if (currentRole === 'owner') {
  const chartData = {
    labels: Object.keys(sales.categories),
    datasets: [
      {
        data: Object.values(sales.categories),
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-black flex flex-col text-white">
      <header className="bg-yellow-900 p-6 text-center">
        <h1 className="text-3xl font-bold">Owner Dashboard</h1>
      </header>
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="space-y-12">
          {/* Today's Sales Summary */}
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h2 className="text-3xl font-bold mb-8 text-center">Today's Total Sales</h2>
            <p className="text-5xl font-bold text-green-400 text-center mb-8">₹{sales.total}</p>
            
            <h3 className="text-2xl font-bold mb-6 text-center">Sales by Category</h3>
            <div className="w-full max-w-md mx-auto">
              <Pie 
                data={chartData} 
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: 'white',
                        font: { size: 16 },
                        padding: 20,
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Table Overview Grid */}
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h2 className="text-3xl font-bold mb-8 text-center">Table Status Overview</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {tables.map((t) => {
                const status = tableStatuses[t];
                const td = getTableData(t);
                const subtotal = calculateSubtotal(td.confirmedItems || []);
                const tax = Math.round(subtotal * TAX_RATE);
                const tip = td.tip || 0;
                const tableTotal = subtotal + tax + tip;
                const isPaid = td.paid;

                return (
                  <div
                    key={t}
                    className={`rounded-2xl p-6 text-center shadow-lg transition ${
                      status === 'vacant'
                        ? 'bg-gray-700 text-gray-400'
                        : isPaid
                        ? 'bg-blue-600 text-white'
                        : 'bg-green-600 text-white'
                    }`}
                  >
                    <p className="text-3xl font-bold">Table {t}</p>
                    <p className="text-xl mt-4 capitalize">{status}</p>
                    {status === 'occupied' && !isPaid && (
                      <p className="text-2xl mt-4 font-bold">₹{tableTotal}</p>
                    )}
                    {isPaid && <p className="text-xl mt-4">Paid</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Active Tables Detailed View */}
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h2 className="text-3xl font-bold mb-8 text-center">Active Orders (Table-wise)</h2>
            {tables.filter((t) => tableStatuses[t] === 'occupied').length === 0 ? (
              <p className="text-center text-2xl text-gray-400">No active tables</p>
            ) : (
              <div className="space-y-10">
                {tables
                  .filter((t) => tableStatuses[t] === 'occupied')
                  .map((t) => {
                    const td = getTableData(t);
                    const subtotal = calculateSubtotal(td.confirmedItems || []);
                    const tax = Math.round(subtotal * TAX_RATE);
                    const total = subtotal + tax + (td.tip || 0);

                    return (
                      <div key={t} className="border-2 border-green-600 rounded-2xl p-6">
                        <h3 className="text-3xl font-bold mb-4 text-center">Table {t}</h3>
                        {td.paid ? (
                          <p className="text-2xl text-center text-blue-400 font-bold">Bill Paid (₹{total})</p>
                        ) : td.billRequested ? (
                          <p className="text-2xl text-center text-orange-400 font-bold">Bill Requested (₹{total})</p>
                        ) : (
                          <>
                            <div className="space-y-4 mb-6">
                              {td.confirmedItems.map((entry, idx) => (
                                <div key={idx} className="bg-gray-700 rounded-xl p-4 flex justify-between">
                                  <div>
                                    <p className="text-xl font-semibold">
                                      {entry.qty} × {entry.item.name}
                                    </p>
                                    <p className="text-gray-300">
                                      {Object.entries(entry.selectedCustom || {})
                                        .map(([_, v]) => `${v}`)
                                        .join(' | ')}
                                      {entry.note && ` | Note: ${entry.note}`}
                                    </p>
                                    <p className="text-lg mt-2 capitalize">Status: {entry.status}</p>
                                  </div>
                                  <p className="text-xl font-bold">₹{calculateItemPrice(entry.item, entry.qty, entry.selectedCustom || {})}</p>
                                </div>
                              ))}
                            </div>
                            <div className="text-right space-y-2 text-xl">
                              <p>Subtotal: ₹{subtotal}</p>
                              <p>Tax (5%): ₹{tax}</p>
                              <p>Tip: ₹{td.tip || 0}</p>
                              <p className="text-3xl font-bold border-t-2 border-gray-600 pt-4">Total: ₹{total}</p>
                            </div>
                            {td.chefNotes.length > 0 && (
                              <div className="mt-6 border-t-2 border-gray-600 pt-6">
                                <h4 className="text-xl font-bold mb-3">Chef Notes</h4>
                                <ol className="list-decimal list-inside space-y-2 text-gray-300">
                                  {td.chefNotes.map((note, i) => (
                                    <li key={i}>{note}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Logout Button */}
          <button
            onClick={() => {
              setCurrentRole(null);
              setCurrentTable(null);
            }}
            className="w-full bg-red-600 text-white py-6 rounded-2xl text-3xl font-bold shadow-lg hover:bg-red-500"
          >
            Logout / Switch Role
          </button>
        </div>
      </div>
    </div>
  );
}
  // Fallback (should not reach here)
  return null;
}